import React from "react";

import { add, format } from "date-fns";

import { API, graphqlOperation } from "aws-amplify";
import { getAccount } from "../../graphql/queries";

import { SelectInput, useListContext } from "react-admin";
import { useForm } from "react-final-form";

import { AccountDropDown } from "../../components/common/AccountDropDown";
import { CustomField } from "../../components/common/CustomFields";
import { logError } from "../../utils/appUtils";

export const handleAccountChange = async (value, setAccount, setAccountId) => {
  const accountId = value?.id;
  setAccountId(accountId);
  try {
    if (!accountId) {
      setAccount("");
      return;
    }
    const response = await API.graphql(
      graphqlOperation(getAccount, { id: accountId }),
    );
    setAccount(response.data.getAccount);
  } catch (err) {
    logError("InboundInvoiceReport:ListActions:handleAccountChange", err);
  }
};

function generateYearArray(startYear) {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: currentYear - startYear + 1 }, (_, index) =>
    (startYear + index).toString(),
  );
}

export const YearSelect = ({
  year,
  setYear,
  validate,
  allowEmpty,
  resettable,
  disabled,
  ...props
}) => {
  const form = useForm();
  const yearList = generateYearArray(
    new Date(window.appUser?.reloadYard.createdAt).getFullYear(),
  );
  const normalizedYear = year || "";

  React.useEffect(() => {
    const currentYearValue = form.getFieldState("year")?.value || "";
    if (currentYearValue !== normalizedYear) {
      form.change("year", normalizedYear);
    }
  }, [form, normalizedYear]);

  React.useEffect(() => {
    if (!validate) {
      form.resetFieldState("year");
    }
  }, [form, validate]);

  return (
    <SelectInput
      id="year"
      source="year"
      label="Year"
      choices={yearList.map((y) => ({ id: y, name: y }))}
      defaultValue=""
      onChange={(evt) => setYear(evt?.target?.value || "")}
      variant="outlined"
      margin="dense"
      fullWidth
      validate={validate}
      allowEmpty={allowEmpty}
      resettable={resettable}
      disabled={disabled}
      alwaysOn
    />
  );
};

// Account picker for the report lists that run off a `search*` query: the
// source has to be the filter path the data provider merges into the query
// filter (e.g. "accountId.eq"), and `onAccountChange` hands the caller the
// whole account, which the custom field columns need for their definitions.
export const AccountFilter = ({ source, onAccountChange, ...props }) => {
  const { filterValues } = useListContext();
  const selectedAccountId = source
    .split(".")
    .reduce((value, key) => (value ? value[key] : value), filterValues);

  // The list restores its filters from the store on a reload, but
  // AccountDropDown only emits onAccountChange on user input. Keep the id in a
  // ref so the accounts fetch can hand the account object back without
  // re-running that fetch every time the filter changes.
  const selectedAccountIdRef = React.useRef(selectedAccountId);
  selectedAccountIdRef.current = selectedAccountId;

  const handleAccountsLoaded = React.useCallback(
    (accounts) => {
      const restoredId = selectedAccountIdRef.current;
      if (!restoredId) return;
      onAccountChange(accounts?.find((itm) => itm.id === restoredId) || null);
    },
    [onAccountChange],
  );

  return (
    <AccountDropDown
      {...props}
      source={source}
      label="Account"
      helperText={false}
      onAccountChange={onAccountChange}
      onAccountsLoaded={handleAccountsLoaded}
    />
  );
};

// The account's custom field definitions as report columns. CustomField
// resolves the record's own index for the field, so the source here only has
// to be a column key that is stable across records.
export const getCustomFieldColumns = (accountFields = []) =>
  accountFields.map((field, index) => (
    <CustomField
      key={field.id}
      source={`customFields[${index}]`}
      field={field}
      label={field.name}
      sortable={false}
    />
  ));

export const createDateRangeParams = (year, monthRange) => {
  const startDate = new Date(`${year}-${monthRange[0]}-01`);
  const duration = monthRange[1] - monthRange[0] + 1;
  const endDate = format(add(startDate, { months: duration }), "yyyy-MM-dd");
  return { startDate: format(startDate, "yyyy-MM-dd"), endDate };
};
